import Axios, { UnauthenticatedAxios } from 'helpers/Interceptor'
import { PlanType } from 'modules/Const' // Assuming PlanType is here
import { BillingCycle } from 'components/shareable/UpgradeToProModal/utils' // Use the shared enum instead of local type

export interface CreatePaymentIntentRequest {
    type: 'campaign' | 'subscription' | 'campaign-with-subscription';
    brandId: number;
    userId?: number;
    planType?: PlanType;
    billingCycle?: BillingCycle;
    campaignInput?: any;
    budget?: number;
    currency?: string;
    campaignId?: string; // Optional: For updating existing campaigns (agent campaigns)
}

export interface CreatePaymentIntentResponse {
    clientSecret: string;
    customerSessionSecret: string;
    stripeCustomerId: string;
    paymentIntentId?: string; // Optional: Not used for subscriptions
    subscriptionId?: string; // Optional: Used for subscription types
    calculatedFees?: any;
    requiresConfirmation: boolean;
    message?: string;
    type: 'payment_intent' | 'subscription'; // Indicates what type of payment this is
}

export interface UpdatePaymentIntentRequest {
    paymentIntentId: string;
    brandId: number;
    userId?: number;
    planType?: PlanType;
    billingCycle?: BillingCycle;
    taskPackSize?: number;
    budget?: number;
    currency?: string;
}

export interface UpdatePaymentIntentResponse {
    clientSecret: string;
    paymentIntentId: string;
    newAmount: number;
    message: string;
}

export interface ConfirmPaymentRequest {
    paymentIntentId?: string; // Optional: for campaign types
    subscriptionId?: string; // Optional: for subscription types
    campaignInput?: any;
    brandId: number; // Or any other necessary identifiers for fulfillment
    campaignId?: string; // Optional: For updating existing campaigns (agent campaigns)
}

export interface ConfirmPaymentResponse {
    success: boolean;
    campaignId?: number;        // If a campaign was created/updated
    subscriptionUpdated?: boolean; // If a subscription was modified
    tasksAdded?: number;           // If tasks were added
    newTier?: string;              // If the user's plan tier changed
    organizationId?: number;       // For team plans - organization that was created
    message: string;               // General success or error message
}

/** Request parameters for automation fee calculation */
export interface AutomationFeeRequest {
    /** Ad platform type */
    adPlatform: 'META' | 'YOUTUBE' | 'TIKTOK' | 'PLAYLISTS';
    /** Campaign budget amount */
    budget: number;
    /** Currency code - defaults to 'USD' */
    currency?: string;
    /** Whether the campaign uses express setup - defaults to false */
    usesExpressSetup?: boolean;
    /** Playbook type - backend determines playlisting vs automation fee logic */
    playbookType?: string;
}

/** Response data for automation fee calculation */
export interface AutomationFeeResponse {
    /** Total amount customer pays */
    totalCost: number;
    /** Amount that goes to actual ads */
    adsBudget: number;
    /** Automation fee amount */
    takeRate: number;
    /** Detailed breakdown of line items */
    lineItems: {
        /** Display name for the line item */
        name: string;
        /** Description of what this line item covers */
        description: string;
        /** Amount for this line item */
        amount: number;
        /** Currency code */
        currency: string;
        /** Type of line item */
        type: 'marketing_budget' | 'automation_fee';
    }[];
}

/** Request parameters for direct campaign charging */
export interface DirectCampaignChargeRequest {
    brandId: number;
    campaignInput: any;
    currency?: string;
    usesExpressSetup?: boolean;
    campaignId?: string;
}

/** Response data for direct campaign charging */
export interface DirectCampaignChargeResponse {
    success: boolean;
    paymentIntentId: string;
    campaignId?: string;
    totalCost: number;
    message: string;
}

/** Response data for payment method information */
export interface PaymentMethodResponse {
    hasPaymentMethod: boolean;
    paymentMethod?: {
        id: string;
        type: string;
        card?: {
            brand: string;
            last4: string;
            exp_month: number;
            exp_year: number;
        };
    };
    message?: string;
}

/**
 * Creates a payment intent for any payment type.
 * Replaces all separate payment creation APIs.
 */
export const createPaymentIntent_api = async (
    request: CreatePaymentIntentRequest
): Promise<CreatePaymentIntentResponse> => {
    const response = await Axios.post<CreatePaymentIntentResponse>(
        '/v2/payments/create-intent', // Ensure this matches your backend endpoint
        request
    );
    return response.data;
};

/**
 * Creates a setup intent for adding payment methods to organization trial subscriptions
 * This follows Stripe best practices for setup intents
 */
export const createSetupIntent_api = async (
    organizationId: number
): Promise<CreatePaymentIntentResponse> => {
    const response = await Axios.post<CreatePaymentIntentResponse>(
        '/v2/payments/setup-payment-method',
        { organizationId }
    );
    return response.data;
};

export const confirmSetupIntent_api = async (
    setupIntentId: string
): Promise<ConfirmPaymentResponse> => {
    const response = await Axios.post<ConfirmPaymentResponse>(
        '/v2/payments/confirm-setup-intent',
        { setupIntentId }
    );
    return response.data;
};

/**
 * Calculate automation fee for current brand tier
 * Uses the new paymentV2 system for accurate fee calculation
 */
export const calculateAutomationFee_api = async (
    brandId: number,
    params: AutomationFeeRequest
): Promise<AutomationFeeResponse> => {
    try {
        const response = await Axios.post<AutomationFeeResponse>(
            '/v2/payments/calculate-automation-fee',
            { brandId, ...params }
        );
        return response.data;
    } catch (error) {
        console.error('Error calculating automation fee:', error);
        throw error;
    }
};

/**
 * Charge campaign directly using saved payment method
 */
export const chargeCampaignDirect_api = async (
    request: DirectCampaignChargeRequest
): Promise<DirectCampaignChargeResponse> => {
    try {
        const response = await Axios.post<DirectCampaignChargeResponse>(
            '/v2/payments/charge-campaign-direct',
            request
        );
        return response.data;
    } catch (error) {
        console.error('Error charging campaign directly:', error);
        throw error;
    }
};

/**
 * Get default payment method for a brand
 */
export const getDefaultPaymentMethod_api = async (
    brandId: number
): Promise<PaymentMethodResponse> => {
    try {
        const response = await Axios.get<PaymentMethodResponse>(
            `/v2/payments/default-payment-method/${brandId}`
        );
        return response.data;
    } catch (error) {
        console.error('Error getting default payment method:', error);
        throw error;
    }
};

export interface PaymentStatusCheckResponse {
    subscriptionId?: string;
    paymentIntentId?: string;
    newTier?: PlanType;
    status: string;
    completed: boolean;
    error?: string;
    campaignId?: number; // Added for campaign payment redirects
}

/**
 * Checks payment status - used for polling instead of immediate confirmation
 */
export const checkPaymentStatus_api = async (
    paymentIntentId: string
): Promise<PaymentStatusCheckResponse> => {
    const response = await Axios.get(
        `/v2/payments/status/${paymentIntentId}`
    );
    return response.data;
};


/**
 * Updates an existing payment intent when user changes plans or payment details.
 * Replaces the need to create new payment intents for plan changes.
 */
export const updatePaymentIntent_api = async (
    request: UpdatePaymentIntentRequest
): Promise<UpdatePaymentIntentResponse> => {
    const response = await Axios.post<UpdatePaymentIntentResponse>(
        '/v2/payments/update-intent',
        request
    );
    return response.data;
};

/** Request parameters for getting campaign plan details */
export interface CampaignPlanDetailsRequest {
    /** Ad platform type */
    adPlatform: 'META' | 'YOUTUBE' | 'TIKTOK' | 'PLAYLISTS';
    /** Campaign budget amount */
    budget: number;
    /** Currency code - defaults to 'USD' */
    currency?: string;
    /** Whether the campaign uses express setup - defaults to false */
    usesExpressSetup?: boolean;
}

/** Plan comparison data for each available plan */
export interface PlanComparison {
    /** Plan type identifier */
    planType: string;
    /** Monthly subscription price */
    monthlyPrice: number;
    /** Annual subscription price */
    annualPrice: number;
    /** Annual subscription price per month */
    annualPricePerMonth: number;
    /** Take rate percentage (e.g., 0.15 for 15%) */
    takeRatePercentage: number;
    /** Take rate amount in currency */
    takeRateAmount: number;
    /** Total upfront cost (subscription + take rate) */
    totalUpfrontCost: number;
    /** Budget limit for this plan */
    budgetLimit: number;
    /** Whether the current budget is within this plan's limit */
    withinBudgetLimit: boolean;
    /** Savings compared to most expensive plan */
    savings: number;
    /** Currency code */
    currency: string;
    /** Detailed breakdown of line items */
    lineItems: {
        /** Display name for the line item */
        name: string;
        /** Description of what this line item covers */
        description: string;
        /** Amount for this line item */
        amount: number;
        /** Currency code */
        currency: string;
        /** Type of line item */
        type: 'marketing_budget' | 'ad_fee' | 'subscription_fee';
    }[];
}

/** Response data for campaign plan details calculation */
export interface AvailablePlansForCampaignCheckout {
    /** List of available plan types based on budget */
    availablePlans: string[];
    /** Original ads budget amount */
    adsBudget: number;
    /** Budget converted to USD for calculations */
    budgetInUSD: number;
    /** Currency code */
    currency: string;
    /** Detailed pricing for each available plan */
    planComparisons: PlanComparison[];
    /** Recommended plan type (usually lowest cost) */
    recommendedPlan: string;
    /** Ad platform */
    platform: string;
}

/** Request parameters for billing details */
export interface BillingDetailsRequest {
    brandId?: number;
    organizationId?: number;
    currency?: string;
    includeUsageData?: boolean;
    billingType?: 'brand' | 'team';
}

/** Response data for billing details */
export interface BillingDetailsResponse {
    // Core subscription data
    planType: PlanType | 'organization';
    billingCycle?: BillingCycle;
    subscriptionStatus: string;

    // Billing dates
    nextBillingDate?: string | null;
    trialEndDate?: string | null;
    scheduledCancellationDate?: string | null;

    // Billing period
    billingPeriod: {
        startDate?: string | null;
        endDate?: string | null;
    };

    // Coupons and discounts
    coupons: Array<{
        name: string;
        amount_off?: number;
        percent_off?: number;
        start_date: number;
        end_date: number;
        description: string;
    }>;

    // Pro plan features (only if applicable)
    seats?: {
        totalSeatsUsed: number;
        seatsLimit: number;
        seatsOverage: number;
        seatsOverFee: number;
        pricePerAdditionalSeat?: number;
    };


    // Brands data  
    brands?: {
        totalBrandsUsed: number;
        brandsLimit: number;
        brandsOverage: number;
        brandsOverFee: number;
        pricePerAdditionalBrand?: number;
    };

    baseSubscriptionPrice?: number;
    recurringInterval?: string; // monthly or annual

    // Usage data (only if applicable) 
    usage?: {
        spentToDate: number;
        monthlySpendLimit: number;
        effectiveSpendLimit: number;
        feeTotal: number;
    };

    // Admin links (only for admins)
    adminLinks?: {
        viewSubscriptionUrl?: string;
        viewCustomerUrl?: string;
    };

    // Additional fields to match existing API
    isFreePlan?: boolean;
    disableSpendLimit?: boolean;
    billingCycleWarning?: boolean;

    // Task usage data
    taskUsage?: {
        used: number;
        remaining: number;
        monthlyLimit: number;
        extraTasksAvailable: number;
        resetDate: string;
        planType: string;
        taskPackInfo: {
            packSize: number;
            packPriceCents: number;
        };
    };

    // Cache metadata
    cachedAt: string;
    dataFreshness: 'live' | 'cached' | 'stale';
}

/**
 * Get campaign plan details with pricing for all available plans
 * @param brandId - Brand ID to calculate plan details for
 * @param params - Plan calculation parameters
 * @returns Promise containing plan details and pricing or throws error
 */
export const getAvailablePlansForCampaignCheckout = async (
    brandId: number,
    params: CampaignPlanDetailsRequest
): Promise<AvailablePlansForCampaignCheckout> => {
    try {
        const response = await Axios.post<AvailablePlansForCampaignCheckout>(
            `/v2/payments/get-available-plans-for-campaign`,
            { brandId, ...params }
        );

        return response.data;
    } catch (error) {
        console.error('Error getting campaign plan details:', error);
        throw error;
    }
};

/**
 * Get comprehensive billing details for a brand with intelligent caching
 * Replaces the problematic getBrandCurrentSubscriptionData_api
 * @param params - Billing details request parameters
 * @returns Promise containing billing details or throws error
 */
export const getBillingDetails_api = async (
    params: BillingDetailsRequest
): Promise<BillingDetailsResponse> => {
    try {
        const { brandId, organizationId, currency = 'USD', includeUsageData = false } = params;

        const queryParams = new URLSearchParams({
            currency,
            organizationId: organizationId?.toString() || '',
            brandId: brandId?.toString() || '',
            includeUsage: includeUsageData.toString()
        });

        const response = await Axios.get<BillingDetailsResponse>(
            `/v2/payments/billing-details?${queryParams}`
        );

        return response.data;
    } catch (error) {
        console.error('Error getting billing details:', error);
        throw error;
    }
};

/** Request parameters for changing subscription plan */
export interface ChangeSubscriptionPlanRequest {
    brandId: number;
    newPlanType: PlanType;
    newBillingCycle: BillingCycle;
    prorationBehavior?: 'create_prorations' | 'none' | 'always_invoice';
}

/** Response data for subscription plan change */
export interface ChangeSubscriptionPlanResponse {
    success: boolean;
    newPlanType: PlanType;
    newBillingCycle: BillingCycle;
    effectiveDate: string;
    prorationAmount?: number;
    meteredUsageChanged: boolean;
    message: string;
}

/**
 * Change subscription plan between supported plan types
 * @param request - Plan change request parameters
 * @returns Promise containing plan change details or throws error
 */
export const changeSubscriptionPlan_api = async (
    request: ChangeSubscriptionPlanRequest
): Promise<ChangeSubscriptionPlanResponse> => {
    try {
        const response = await Axios.post<ChangeSubscriptionPlanResponse>(
            `/v2/payments/change-plan/${request.brandId}`,
            {
                newPlanType: request.newPlanType,
                newBillingCycle: request.newBillingCycle,
                prorationBehavior: request.prorationBehavior || 'create_prorations'
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error changing subscription plan:', error);
        throw error;
    }
};

/** Response data for subscription cancellation */
export interface CancelSubscriptionResponse {
    success: boolean;
    status: string;
    cancellationDate: string;
    message: string;
    currentPeriodEnd: string;
}

/** Request parameters for subscription cancellation */
export interface CancelSubscriptionRequest {
    /** Brand ID for brand-specific cancellation */
    brandId?: number;
    /** Organization ID for organization cancellation */
    organizationId?: number;
}

/**
 * Cancel subscription at period end using new paymentV2 system
 * @param params - Object containing either brandId or organizationId
 * @returns Promise containing cancellation details or throws error
 */
export const cancelSubscriptionV2_api = async (
    params: CancelSubscriptionRequest
): Promise<CancelSubscriptionResponse> => {
    try {
        const response = await Axios.post<CancelSubscriptionResponse>(
            '/v2/payments/cancel-subscription',
            params
        );
        return response.data;
    } catch (error) {
        console.error('Error cancelling subscription:', error);
        throw error;
    }
};

/** Response data for subscription resumption */
export interface ResumeSubscriptionResponse {
    success: boolean;
    status: string;
    effectiveDate: string;
    message: string;
}

/** Request parameters for subscription resumption */
export interface ResumeSubscriptionRequest {
    /** Brand ID for brand-specific resumption */
    brandId?: number;
    /** Organization ID for organization resumption */
    organizationId?: number;
}

/**
 * Resume subscription that was scheduled for cancellation using new paymentV2 system
 * @param params - Object containing either brandId or organizationId
 * @returns Promise containing resumption details or throws error
 */
export const resumeSubscriptionV2_api = async (
    params: ResumeSubscriptionRequest
): Promise<ResumeSubscriptionResponse> => {
    try {
        const response = await Axios.post<ResumeSubscriptionResponse>(
            '/v2/payments/resume-subscription',
            params
        );
        return response.data;
    } catch (error) {
        console.error('Error resuming subscription:', error);
        throw error;
    }
};

// === ANONYMOUS PAYMENT FUNCTIONS ===


/**
 * Create a payment intent for anonymous guest checkout
 * @param data - Anonymous payment intent data
 * @returns Promise containing payment intent details or throws error
 */
export const createAnonymousPaymentIntent_api = async (data: {
    temporarySessionId: string;
    campaignInput: any;
    currency?: string;
    campaignId?: string;
}): Promise<CreatePaymentIntentResponse> => {
    try {
        const response = await UnauthenticatedAxios.post<CreatePaymentIntentResponse>(
            '/v2/payments/create-anonymous-intent',
            data
        );
        return response.data;
    } catch (error: any) {
        console.error('Error creating anonymous payment intent:', error);

        // Handle specific error cases
        if (error.response?.status === 400) {
            throw new Error(error.response.data?.message || 'Invalid payment data. Please check your information.');
        } else if (error.response?.status === 404) {
            throw new Error('Session expired. Please refresh the page and try again.');
        } else if (error.response?.status === 429) {
            throw new Error('Too many requests. Please wait a moment and try again.');
        } else {
            throw new Error('Failed to create payment. Please try again.');
        }
    }
};

/**
 * Confirm anonymous payment after Stripe client-side confirmation
 * @param data - Anonymous payment confirmation data
 * @returns Promise containing confirmation details or throws error
 */
export const confirmAnonymousPayment_api = async (data: {
    paymentIntentId: string;
    temporarySessionId: string;
    campaignInput: any;
    email?: string;
}): Promise<ConfirmPaymentResponse> => {
    try {
        const response = await UnauthenticatedAxios.post<ConfirmPaymentResponse>(
            '/v2/payments/confirm-anonymous-payment',
            data
        );
        return response.data;
    } catch (error: any) {
        console.error('Error confirming anonymous payment:', error);

        // Handle specific error cases
        if (error.response?.status === 409) {
            throw new Error('An account with this email already exists. Please sign in or use a different email.');
        } else if (error.response?.status === 400) {
            throw new Error(error.response.data?.message || 'Invalid payment data. Please check your information.');
        } else if (error.response?.status === 404) {
            throw new Error('Session expired. Please refresh the page and try again.');
        } else if (error.response?.status === 429) {
            throw new Error('Too many requests. Please wait a moment and try again.');
        } else {
            throw new Error('Payment confirmation failed. Please try again.');
        }
    }
};

/** Request parameters for unified billing portal */
export interface BillingPortalRequest {
    /** Brand ID for brand-specific billing */
    brandId?: number;
    /** Organization ID for organization billing */
    organizationId?: number;
}

/**
 * Gets unified billing portal URL for both brands and organizations
 * Replaces the old getStripeSubscriptionPortalUrl_api with unified approach
 * 
 * @param params - Object containing either brandId or organizationId
 * @returns Promise resolving to the billing portal URL
 * @throws Error if unable to generate portal URL
 */
export const getBillingPortalUrl_api = async (params: BillingPortalRequest): Promise<string> => {
    try {
        const response = await Axios.post<{ url: string }>('/v2/payments/billing-portal', params);
        return response.data.url;
    } catch (error) {
        console.error('Error getting billing portal URL:', error);
        throw error;
    }
};

/**
 * Handles organization payment updates with automatic subscription migration
 * This endpoint ensures legacy subscriptions are migrated to TEAM_V2 pricing
 * and billing is properly reconciled before returning billing portal URL
 * 
 * @param params - Object containing organizationId
 * @returns Promise resolving to the billing portal URL and migration details
 * @throws Error if unable to handle payment update
 */
export const handleOrganizationPaymentUpdate_api = async (params: { organizationId: number }): Promise<{
    url: string;
    migrationPerformed?: boolean;
    migrationDetails?: any;
}> => {
    try {
        const response = await Axios.post('/v2/payments/organization-payment-update', params);
        return response.data;
    } catch (error) {
        console.error('Error handling organization payment update:', error);
        throw error;
    }
};

/** Request parameters for cancellation portal */
export interface CancellationPortalRequest {
    /** Brand ID for brand-specific cancellation */
    brandId?: number;
    /** Organization ID for organization cancellation */
    organizationId?: number;
}

/** Response data for cancellation portal */
export interface CancellationPortalResponse {
    url: string;
    message: string;
}

/**
 * Get Stripe cancellation portal URL with retention coupon
 * Creates a direct link to Stripe's cancellation flow with retention offers
 * 
 * @param params - Object containing either brandId or organizationId
 * @returns Promise containing cancellation portal URL
 * @throws Error if unable to create cancellation portal
 */
export const getCancellationPortal_api = async (
    params: CancellationPortalRequest
): Promise<CancellationPortalResponse> => {
    try {
        const response = await Axios.post<CancellationPortalResponse>(
            '/v2/payments/get-cancellation-portal',
            params
        );
        return response.data;
    } catch (error) {
        console.error('Error getting cancellation portal:', error);
        throw error;
    }
};
